import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Collapse,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { ExcelData, ColumnMappingData } from '../../pages/ExcelUploadPage';
import { ManualEntry } from './ManualFieldEntry';
import { getLegNumber, findMappedColumnIndex, isLegPresent, mappedLegNumbers, getMappedCellValue } from './legPresence';
import { hasIsolatedCountryCode, duplicateCountryCodeIssue } from './countryCodeInAddress';
import * as XLSX from 'xlsx';

interface ValidationIssue {
  rowIndex: number;
  column: string;
  fieldName: string;
  issue: string;
  severity: 'error' | 'warning';
  value: string;
}

interface DataValidationButtonProps {
  excelData: ExcelData;
  mappings: ColumnMappingData;
  manualEntries?: ManualEntry[];
  skipFirstRow?: boolean;
  onSkipErrorRows?: (skipErrorRows: boolean, errorRowIndices: number[]) => void;
  disabled?: boolean;
}

/** Unique 1-based row indices that have data errors (excludes mapping errors at rowIndex 0). */
const getErrorRowIndices = (errors: ValidationIssue[]): number[] =>
  Array.from(new Set(errors.filter((issue) => issue.rowIndex > 0).map((issue) => issue.rowIndex)));

// ── Pure per-field validation helpers (no hooks; behavior-preserving) ──────────

/** Context shared by every per-field validator for a single cell. */
interface FieldCtx {
  rowIndex: number; // already offset for display (rowIndex + 2 / +1)
  column: string;
  fieldId: string;
  trimmedValue: string;
  legMode?: string; // this leg's transport mode (for mode-conditional rules)
  countryCode?: string; // this side's mapped Country Code (for the free-address duplicate rule)
}

const makeIssue = (
  ctx: FieldCtx,
  issue: string,
  severity: ValidationIssue['severity']
): ValidationIssue => ({
  rowIndex: ctx.rowIndex,
  column: ctx.column,
  fieldName: ctx.fieldId,
  issue,
  severity,
  value: ctx.trimmedValue,
});

const WEIGHT_FIELDS = new Set(['weight_kg', 'weight', 'cargo_weight']);
const REQUIRED_FIELD_NAMES = new Set(['weight_kg', 'weight', 'cargo_weight', 'transport_mode']);
const ORIGIN_LOC_FIELDS = new Set(['origin_country', 'origin_city', 'origin_postal_code', 'origin_free_address']);
const DEST_LOC_FIELDS = new Set(['destination_country', 'destination_city', 'destination_postal_code', 'destination_free_address']);
const VALID_TRANSPORT_MODES = ['air', 'sea', 'rail', 'ferry', 'iww', 'truck', 'road'];
const ROAD_MODES = new Set(['road', 'truck']);
const FREE_ADDRESS_FIELDS = new Set(['origin_free_address', 'destination_free_address']);

// Kept in sync with the inline layer (useDataValidation) — both suites assert these strings.
const LOAD_FACTOR_EMPTY_ISSUE = 'Load factor is mapped but empty — the default 80% will be used';
const nonRoadLoadFactorIssue = (legMode: string): string =>
  `Load factor only applies to Road/Truck legs — this leg's transport mode is '${legMode}'`;

/** Weight: invalid-number (error), zero (warning), negative (error). */
const validateWeight = (ctx: FieldCtx): ValidationIssue[] => {
  const { trimmedValue } = ctx;
  const weight = Number.parseFloat(trimmedValue.replace(',', '.'));
  if (trimmedValue && (/[a-zA-Z]/.test(trimmedValue) || Number.isNaN(weight))) {
    return [makeIssue(ctx, 'Weight is not a valid number', 'error')];
  }
  if (weight === 0) {
    return [makeIssue(ctx, 'Weight is zero', 'warning')];
  }
  if (weight < 0) {
    return [makeIssue(ctx, 'Weight is negative', 'error')];
  }
  return [];
};

const validateTransportMode = (ctx: FieldCtx): ValidationIssue[] => {
  const { trimmedValue } = ctx;
  if (trimmedValue && !VALID_TRANSPORT_MODES.includes(trimmedValue.toLowerCase())) {
    return [makeIssue(ctx, `Invalid transport mode. Must be one of: ${VALID_TRANSPORT_MODES.join(', ')}`, 'error')];
  }
  return [];
};

const validateShipmentDate = (ctx: FieldCtx): ValidationIssue[] => {
  const { trimmedValue } = ctx;
  if (!trimmedValue) return [];
  const hasSeparator = /[.\-/\s]/.test(trimmedValue);
  if (hasSeparator) {
    const parts = trimmedValue.split(/[.\-/]/);
    const manualValid = parts.length === 3 && !Number.isNaN(new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime());
    const nativeValid = !Number.isNaN(new Date(trimmedValue).getTime());
    if (!manualValid && !nativeValid) {
      return [makeIssue(ctx, 'Not a valid date', 'error')];
    }
    return [];
  }
  return [makeIssue(ctx, 'Not a valid date', 'error')];
};

const validateCountry = (ctx: FieldCtx): ValidationIssue[] => {
  const { trimmedValue } = ctx;
  if (trimmedValue && !/^[A-Za-z]+$/.test(trimmedValue)) {
    return [makeIssue(ctx, 'Country code must contain only letters', 'error')];
  }
  if (trimmedValue && trimmedValue.length !== 2) {
    return [makeIssue(ctx, 'Country code must be exactly 2 characters', 'error')];
  }
  return [];
};

const validatePostalCode = (ctx: FieldCtx): ValidationIssue[] => {
  const { trimmedValue } = ctx;
  const out: ValidationIssue[] = [];
  if (trimmedValue && trimmedValue.length > 10) {
    out.push(makeIssue(ctx, 'Postal code is too long (max 10 characters)', 'warning'));
  }
  if (trimmedValue && !/^[A-Z0-9\s-]+$/i.test(trimmedValue)) {
    out.push(makeIssue(ctx, 'Postal code contains invalid characters', 'warning'));
  }
  return out;
};

const validateCooling = (ctx: FieldCtx): ValidationIssue[] => {
  const { trimmedValue } = ctx;
  if (trimmedValue && !['true', 'false', 'yes', 'no'].includes(trimmedValue.toLowerCase())) {
    return [makeIssue(ctx, 'Cooling must be true/false or yes/no', 'error')];
  }
  return [];
};

const validateEmptyRequired = (ctx: FieldCtx, fieldName: string): ValidationIssue[] => {
  if (ctx.trimmedValue) return [];
  if (REQUIRED_FIELD_NAMES.has(fieldName)) {
    return [makeIssue(ctx, 'Required field is empty', 'error')];
  }
  if (ORIGIN_LOC_FIELDS.has(fieldName) || DEST_LOC_FIELDS.has(fieldName)) {
    return [makeIssue(ctx, 'Location field is empty', 'warning')];
  }
  return [];
};

/** Load factor: road/truck legs only, 0-100. Blank/unmapped mode defaults to Truck
 * on the backend, so it counts as road here. */
const validateLoadFactor = (ctx: FieldCtx): ValidationIssue[] => {
  const { trimmedValue } = ctx;
  const legMode = ctx.legMode ?? '';
  const isRoadLeg = !legMode || ROAD_MODES.has(legMode.toLowerCase());
  if (!trimmedValue) {
    // Only worth mentioning on a leg that would actually use it.
    return isRoadLeg ? [makeIssue(ctx, LOAD_FACTOR_EMPTY_ISSUE, 'warning')] : [];
  }
  const out: ValidationIssue[] = [];
  if (!isRoadLeg) {
    out.push(makeIssue(ctx, nonRoadLoadFactorIssue(legMode), 'warning'));
  }
  const value = Number.parseFloat(trimmedValue.replace(',', '.'));
  if (/[a-zA-Z]/.test(trimmedValue) || Number.isNaN(value)) {
    out.push(makeIssue(ctx, 'Not a valid number', 'error'));
  } else if (value < 0 || value > 100) {
    out.push(makeIssue(ctx, 'Load factor must be between 0 and 100', 'error'));
  }
  return out;
};

/** The leg's own Country Code column already carries the country, so repeating it in the
 * Free Address is redundant data rather than a hard error. */
const validateFreeAddress = (ctx: FieldCtx): ValidationIssue[] => {
  const { trimmedValue, countryCode } = ctx;
  if (!trimmedValue || !countryCode || !hasIsolatedCountryCode(trimmedValue, countryCode)) return [];
  return [makeIssue(ctx, duplicateCountryCodeIssue(countryCode), 'warning')];
};

/** Run every applicable rule for one field, preserving original ordering. */
const validateField = (ctx: FieldCtx): ValidationIssue[] => {
  const fieldName = ctx.fieldId.replace(/_leg\d+$/, '');
  const out: ValidationIssue[] = [];
  if (WEIGHT_FIELDS.has(fieldName)) out.push(...validateWeight(ctx));
  if (fieldName === 'transport_mode') out.push(...validateTransportMode(ctx));
  if (fieldName === 'shipment_date' || fieldName === 'shipmentDate') out.push(...validateShipmentDate(ctx));
  if (fieldName === 'origin_country' || fieldName === 'destination_country' || fieldName === 'country') {
    out.push(...validateCountry(ctx));
  }
  if (fieldName === 'origin_postal_code' || fieldName === 'destination_postal_code' || fieldName === 'postal_code') {
    out.push(...validatePostalCode(ctx));
  }
  if (fieldName === 'cooling') out.push(...validateCooling(ctx));
  if (fieldName === 'load_factor') out.push(...validateLoadFactor(ctx));
  if (FREE_ADDRESS_FIELDS.has(fieldName)) out.push(...validateFreeAddress(ctx));
  out.push(...validateEmptyRequired(ctx, fieldName));
  return out;
};

/** Validate one mapped column for one data row; returns issues for all its fields. */
const validateColumn = (
  excelData: ExcelData,
  rowIndex: number,
  row: (string | number)[],
  colKey: string,
  rawFieldId: string,
  presentLegs: ReadonlySet<number>,
  legModes: ReadonlyMap<number, string>,
  legCountries: ReadonlyMap<string, string>
): ValidationIssue[] => {
  if (colKey.startsWith('MANUAL:')) return [];
  const columnIndex = Number.parseInt(colKey, 10);
  if (Number.isNaN(columnIndex) || columnIndex < 0 || columnIndex >= excelData.headers.length) return [];

  const trimmedValue = String(row[columnIndex] || '').trim();
  const column = `Col ${columnIndex + 1} (${excelData.headers[columnIndex]})`;
  const displayRow = rowIndex + 2; // +1 header row, +1 for 1-based indexing

  const out: ValidationIssue[] = [];
  for (const fieldId of rawFieldId.split('||').filter(Boolean)) {
    // Legs ≥ 2 are optional per row: skip fields of legs the backend would not
    // create for this row (leg 1 is the mandatory base leg, always validated).
    const legNumber = getLegNumber(fieldId);
    if (legNumber && legNumber >= 2 && !presentLegs.has(legNumber)) continue;
    const legMode = legNumber ? legModes.get(legNumber) : undefined;
    const fieldName = fieldId.replace(/_leg\d+$/, '');
    const countryCode =
      legNumber && FREE_ADDRESS_FIELDS.has(fieldName)
        ? legCountries.get(`${fieldName.replace('_free_address', '_country')}_leg${legNumber}`)
        : undefined;
    out.push(...validateField({ rowIndex: displayRow, column, fieldId, trimmedValue, legMode, countryCode }));
  }
  return out;
};

/** Column carrying a leg's transport mode, else the first column mapping any of its fields. */
const legAnchorColumn = (mappings: ColumnMappingData, legNumber: number): { columnIndex: number; fieldId: string } | null => {
  const modeFieldId = `transport_mode_leg${legNumber}`;
  const modeIndex = findMappedColumnIndex(mappings, modeFieldId);
  if (modeIndex !== null) return { columnIndex: modeIndex, fieldId: modeFieldId };
  for (const [colKey, rawFieldId] of Object.entries(mappings)) {
    if (colKey.startsWith('MANUAL:')) continue;
    const columnIndex = Number.parseInt(colKey, 10);
    if (Number.isNaN(columnIndex)) continue;
    const fieldId = rawFieldId.split('||').find((f) => getLegNumber(f) === legNumber);
    if (fieldId) return { columnIndex, fieldId };
  }
  return null;
};

/** Row-level leg checks: orphaned transport modes and non-sequential legs. */
const validateLegShape = (
  excelData: ExcelData,
  rowIndex: number,
  row: (string | number)[],
  mappings: ColumnMappingData,
  legNumbers: number[],
  presentLegs: ReadonlySet<number>
): ValidationIssue[] => {
  const out: ValidationIssue[] = [];
  const displayRow = rowIndex + 2;
  const columnLabel = (columnIndex: number) => `Col ${columnIndex + 1} (${excelData.headers[columnIndex]})`;

  const checkLeg = (legNumber: number): ValidationIssue | null => {
    if (legNumber < 2) return null;
    if (presentLegs.has(legNumber)) {
      // A filled leg must not follow an empty one (legs are filled from Leg 1 up).
      const gap = legNumbers.find((n) => n < legNumber && !presentLegs.has(n));
      if (gap === undefined) return null;
      const anchor = legAnchorColumn(mappings, legNumber);
      if (!anchor) return null;
      return {
        rowIndex: displayRow,
        column: columnLabel(anchor.columnIndex),
        fieldName: anchor.fieldId,
        issue: `Leg ${legNumber} is filled but leg ${gap} is empty — legs must be filled in order`,
        severity: 'error',
        value: '',
      };
    }
    // A transport mode without origin/destination marks a leg the backend
    // would silently drop.
    const modeIndex = findMappedColumnIndex(mappings, `transport_mode_leg${legNumber}`);
    const modeValue = modeIndex === null ? '' : String(row[modeIndex] ?? '').trim();
    if (modeIndex === null || !modeValue) return null;
    return {
      rowIndex: displayRow,
      column: columnLabel(modeIndex),
      fieldName: `transport_mode_leg${legNumber}`,
      issue: `Transport mode set but leg ${legNumber} has no origin/destination`,
      severity: 'error',
      value: modeValue,
    };
  };

  for (const legNumber of legNumbers) {
    const issue = checkLeg(legNumber);
    if (issue) out.push(issue);
  }
  return out;
};

/** Field ids whose presence (mapped or manual) satisfies the required-field check. */
const collectMissingRequiredFields = (
  mappings: ColumnMappingData,
  manualEntries: ManualEntry[]
): string[] => {
  const mappedFieldIds: string[] = [];
  for (const value of Object.values(mappings)) {
    mappedFieldIds.push(...value.split('||').filter(Boolean));
  }
  const manualFieldIds = manualEntries
    .filter((entry) => entry.fieldId && entry.value)
    .map((entry) => entry.fieldId);
  const allProvidedFields = new Set([...mappedFieldIds, ...manualFieldIds]);

  const missing = ['weight_kg', 'transport_mode_leg1'].filter((fieldId) => !allProvidedFields.has(fieldId));

  const originFields = ['origin_country_leg1', 'origin_city_leg1', 'origin_postal_code_leg1', 'origin_free_address_leg1'];
  const destFields = ['destination_country_leg1', 'destination_city_leg1', 'destination_postal_code_leg1', 'destination_free_address_leg1'];
  if (!originFields.some((f) => allProvidedFields.has(f))) missing.push('origin_address_leg1');
  if (!destFields.some((f) => allProvidedFields.has(f))) missing.push('destination_address_leg1');
  return missing;
};

const MISSING_FIELD_NAMES: Record<string, string> = {
  weight_kg: 'Weight (kg)',
  transport_mode_leg1: 'Transport Mode (Leg 1)',
  origin_address_leg1: 'Origin address (Leg 1) — map at least one of: country, city, postal code, or free address',
  destination_address_leg1: 'Destination address (Leg 1) — map at least one of: country, city, postal code, or free address',
};

const buildMappingErrors = (missingRequiredFields: string[]): ValidationIssue[] =>
  missingRequiredFields.map((fieldId) => ({
    rowIndex: 0, // Row 0 indicates mapping issue, not data issue
    column: 'Not Mapped',
    fieldName: fieldId,
    issue: `Required field "${MISSING_FIELD_NAMES[fieldId] || fieldId}" is not mapped to any column or set manually`,
    severity: 'error',
    value: '',
  }));

/** Validate every data row against the mappings; returns all data issues. */
const validateRows = (
  excelData: ExcelData,
  mappings: ColumnMappingData,
  skipFirstRow: boolean,
  manualEntries: ManualEntry[]
): ValidationIssue[] => {
  const out: ValidationIssue[] = [];
  const legNumbers = mappedLegNumbers(mappings);
  const manualFieldIds = new Set(
    manualEntries.filter((entry) => entry.fieldId && entry.value).map((entry) => entry.fieldId)
  );
  excelData.rows.forEach((row, rowIndex) => {
    if (skipFirstRow && rowIndex === 0) return;
    const presentLegs = new Set(
      legNumbers.filter((n) => isLegPresent(row, mappings, n, manualFieldIds))
    );
    // Each leg's transport mode for this row (used by mode-conditional rules like load factor).
    const legModes = new Map<number, string>(
      legNumbers.map((n) => [n, getMappedCellValue(row, mappings, `transport_mode_leg${n}`)])
    );
    // Each leg's mapped Country Code per side, for the free-address duplicate rule.
    const legCountries = new Map<string, string>(
      legNumbers.flatMap((n) =>
        ['origin_country', 'destination_country'].map((field): [string, string] => [
          `${field}_leg${n}`,
          getMappedCellValue(row, mappings, `${field}_leg${n}`),
        ])
      )
    );
    Object.entries(mappings).forEach(([colKey, rawFieldId]) => {
      out.push(...validateColumn(excelData, rowIndex, row, colKey, rawFieldId, presentLegs, legModes, legCountries));
    });
    out.push(...validateLegShape(excelData, rowIndex, row, mappings, legNumbers, presentLegs));
  });
  return out;
};

const ValidationSummaryAlert: React.FC<{
  errorCount: number;
  warningCount: number;
  isMappingError: boolean;
}> = ({ errorCount, warningCount, isMappingError }) => {
  if (isMappingError) {
    return (
      <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
        Please map all required fields before validating data. {errorCount} required{' '}
        field{errorCount === 1 ? ' is' : 's are'} not mapped.
      </Alert>
    );
  }
  return (
    <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
      Found {errorCount} error{errorCount === 1 ? '' : 's'} and{' '}
      {warningCount} warning{warningCount === 1 ? '' : 's'} in your data.
      Please review and fix before processing.
    </Alert>
  );
};

const IssueDetailCells: React.FC<{ issue: ValidationIssue; colSpan?: number }> = ({
  issue,
  colSpan,
}) => (
  <>
    <TableCell colSpan={colSpan}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {issue.column}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {issue.fieldName}
      </Typography>
    </TableCell>
    <TableCell>
      <Typography variant="body2">{issue.issue}</Typography>
      {issue.value && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          Value: "{issue.value}"
        </Typography>
      )}
    </TableCell>
  </>
);

export const DataValidationButton: React.FC<DataValidationButtonProps> = ({
  excelData,
  mappings,
  manualEntries = [],
  skipFirstRow = true,
  onSkipErrorRows,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    errors: true,
    warnings: true,
  });
  const [skipErrorRows, setSkipErrorRows] = useState(false);

  const validateData = () => {
    // Re-validation invalidates any previously selected skip list
    setSkipErrorRows(false);
    onSkipErrorRows?.(false, []);

    // First, check if required fields are mapped or provided via manual entries
    const missingRequiredFields = collectMissingRequiredFields(mappings, manualEntries);
    if (missingRequiredFields.length > 0) {
      // Don't proceed with data validation if mappings are incomplete
      setIssues(buildMappingErrors(missingRequiredFields));
      setOpen(true);
      return;
    }

    setIssues(validateRows(excelData, mappings, skipFirstRow, manualEntries));
    setOpen(true);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const downloadErrorsXLSX = () => {
    // Group issues by row number
    const issuesByRow = new Map<number, ValidationIssue[]>();
    issues.forEach(issue => {
      const rowNum = issue.rowIndex;
      if (!issuesByRow.has(rowNum)) {
        issuesByRow.set(rowNum, []);
      }
      issuesByRow.get(rowNum)!.push(issue);
    });

    // Create a map of column indices that have errors for each row
    // Build reverse mapping from fieldName to column index
    const fieldToColIndex: Record<string, number> = {};
    Object.entries(mappings).forEach(([colKey, rawFieldId]) => {
      if (!colKey.startsWith('MANUAL:')) {
        const colIndex = Number.parseInt(colKey, 10);
        for (const fId of rawFieldId.split('||').filter(Boolean)) {
          fieldToColIndex[fId] = colIndex;
        }
      }
    });

    const errorColumnsByRow = new Map<number, Set<number>>();
    issues.forEach(issue => {
      if (!errorColumnsByRow.has(issue.rowIndex)) {
        errorColumnsByRow.set(issue.rowIndex, new Set());
      }
      const colIndex = fieldToColIndex[issue.fieldName];
      if (colIndex !== undefined && colIndex >= 0) {
        errorColumnsByRow.get(issue.rowIndex)!.add(colIndex);
      }
    });

    // Create headers: Original Excel columns + Remarks column
    const headers = [...excelData.headers, 'Remarks (Errors/Warnings)'];

    // Create rows with marked error cells + remarks
    const sheetRows: string[][] = [];

    issuesByRow.forEach((rowIssues, rowIndex) => {
      // For mapping errors (rowIndex === 0), create a special row
      if (rowIndex === 0) {
        const remarks = rowIssues.map(issue =>
          `[${issue.severity.toUpperCase()}] ${issue.fieldName}: ${issue.issue}`
        ).join('; ');

        const row = [
          'MAPPING ERROR',
          ...new Array(excelData.headers.length - 1).fill(''),
          remarks
        ];
        sheetRows.push(row);
      } else {
        // Get the actual row data from Excel (adjust for header row)
        const dataRowIndex = skipFirstRow ? rowIndex - 2 : rowIndex - 1;
        const rowData = excelData.rows[dataRowIndex] || [];
        const errorColumns = errorColumnsByRow.get(rowIndex) || new Set();

        // Mark cells that have errors with ❌ prefix
        const markedRow = rowData.map((cell, colIndex) => {
          const cellValue = String(cell || '');
          if (errorColumns.has(colIndex)) {
            return `❌ ${cellValue}`;
          }
          return cellValue;
        });

        // Build remarks column with all errors for this row
        const remarks = rowIssues.map(issue =>
          `[${issue.severity.toUpperCase()}] ${issue.column}: ${issue.issue}`
        ).join('; ');

        sheetRows.push([...markedRow, remarks]);
      }
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...sheetRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Validation Errors');
    XLSX.writeFile(
      workbook,
      `validation-errors-${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<WarningIcon />}
        onClick={validateData}
        disabled={disabled}
      >
        Pre-Validate Data
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {issues.length === 0 ? (
              <>
                <CheckCircleIcon color="success" />
                <Typography variant="h6">Validation Passed</Typography>
              </>
            ) : (
              <>
                <WarningIcon color="warning" />
                <Typography variant="h6">Validation Issues Found</Typography>
              </>
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {issues.length === 0 ? (
            <Alert severity="success" sx={{ py: 0.5 }}>
              All data looks good! No validation issues found.
            </Alert>
          ) : (
            <Box>
              {/* Check if issues are mapping errors (rowIndex === 0) */}
              <ValidationSummaryAlert
                errorCount={errors.length}
                warningCount={warnings.length}
                isMappingError={issues.some((issue) => issue.rowIndex === 0)}
              />

              {/* Errors Section */}
              {errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      mb: 1,
                    }}
                    onClick={() => toggleSection('errors')}
                  >
                    <IconButton size="small">
                      {expandedSections.errors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Errors ({errors.length})
                    </Typography>
                  </Box>
                  <Collapse in={expandedSections.errors}>
                    <Table size="small">
                      <TableBody>
                        {errors.map((issue) => (
                          <TableRow
                            key={`${issue.rowIndex}-${issue.fieldName}-${issue.issue}`}
                            sx={{ '&:hover': { bgcolor: 'error.50' } }}
                          >
                            {issue.rowIndex > 0 && (
                              <TableCell>
                                <Chip
                                  label={`Row ${issue.rowIndex}`}
                                  size="small"
                                  color="error"
                                  sx={{ minWidth: 70 }}
                                />
                              </TableCell>
                            )}
                            <IssueDetailCells issue={issue} colSpan={issue.rowIndex === 0 ? 2 : 1} />
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Collapse>
                </Box>
              )}

              {/* Warnings Section */}
              {warnings.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      mb: 1,
                    }}
                    onClick={() => toggleSection('warnings')}
                  >
                    <IconButton size="small">
                      {expandedSections.warnings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Warnings ({warnings.length})
                    </Typography>
                  </Box>
                  <Collapse in={expandedSections.warnings}>
                    <Table size="small">
                      <TableBody>
                        {warnings.map((issue) => (
                          <TableRow
                            key={`${issue.rowIndex}-${issue.fieldName}-${issue.issue}`}
                            sx={{ '&:hover': { bgcolor: 'warning.50' } }}
                          >
                            <TableCell>
                              <Chip
                                label={`Row ${issue.rowIndex}`}
                                size="small"
                                color="warning"
                                sx={{ minWidth: 70 }}
                              />
                            </TableCell>
                            <IssueDetailCells issue={issue} />
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 2, p: 3 }}>
          {/* Skip Error Rows Option */}
          {errors.length > 0 && (
            <Box sx={{
              bgcolor: 'warning.50',
              border: '1px solid',
              borderColor: 'warning.main',
              borderRadius: 1,
              p: 2
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipErrorRows}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSkipErrorRows(checked);
                      if (onSkipErrorRows) {
                        // Emit zero-based excelData.rows indices (display row − 2):
                        // the backend filters its rows[] array by raw index.
                        onSkipErrorRows(checked, getErrorRowIndices(errors).map((displayRow) => displayRow - 2));
                      }
                    }}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Skip rows with errors when processing
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`Skip ${getErrorRowIndices(errors).length} row(s) with errors and process only valid data`}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {issues.length > 0 && (
              <Button
                onClick={downloadErrorsXLSX}
                startIcon={<DownloadIcon />}
                variant="outlined"
                color="primary"
              >
                Download Errors (XLSX)
              </Button>
            )}
            <Button
              onClick={() => setOpen(false)}
              color="primary"
              variant="contained"
              sx={{ ml: 'auto' }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
